import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { challengeSchema, type BootstrapResponse } from "../../../api/schemas";
import type { MiningController, MiningChallengeWire } from "../../../mining-engine";
import type { WalletSessionController, WalletSessionSnapshot } from "../../../wallet/wallet-session";
import { ContextChangedError, type WalletOperation } from "../../../wallet/wallet-operation";
import { sameAddress } from "../../../state/model-helpers";
import type { ActionContext } from "../../../state/action-context";
import { actionPermissions } from "../../../state/transaction-capability";
import type { MiningAudio } from "../../../audio/mining-audio";
import type { MiningMode, MiningPowerMode } from "../types";

type Options = ActionContext & {
  walletController: WalletSessionController;
  walletSnapshot: WalletSessionSnapshot;
  miningController: MiningController;
  miningAudio: MiningAudio;
  bootstrap: BootstrapResponse | null;
  activeToken: BootstrapResponse["tokens"][number] | null;
  isTransactionBlocked(): boolean;
};

export function useMiningActions({ walletController, walletSnapshot, miningController, miningAudio, bootstrap, activeToken,
  activeContext, contextKey, readContextKey, beginOperation, endOperation, isTransactionBlocked }: Options) {
  const startup = useRef<{ scope: string; promise: Promise<void>; cancelled: boolean;
    operation: WalletOperation | null } | null>(null);
  const [miningStarting, setMiningStarting] = useState(false);
  const [authExpiring, setAuthExpiring] = useState(false);
  const savedTraversal = useRef<{ scope: string; remaining: string[]; attempted: Set<string>; last: string | null } | null>(null);
  const miningContext = useRef(contextKey);
  useLayoutEffect(() => {
    if (miningContext.current === contextKey) return;
    miningContext.current = contextKey;
    savedTraversal.current = null;
    if (miningController.getSnapshot().session) miningController.pause("WALLET_CONTEXT_CHANGED")
      .catch(() => console.error("Mining context cleanup failed."));
  }, [contextKey, miningController]);
  useEffect(() => {
    setAuthExpiring(false);
    if (!walletSnapshot.authenticated || !walletSnapshot.sessionExpiresAt || !walletController.sessionRemainingMs) return;
    const timer = setTimeout(() => setAuthExpiring(true), Math.max(0, walletController.sessionRemainingMs() - 300_000));
    return () => clearTimeout(timer);
  }, [walletSnapshot.authenticated, walletSnapshot.sessionRevision, walletSnapshot.sessionExpiresAt, walletController]);
  const sessionAction = useRef<Promise<void> | null>(null);
  const discoveryToken = activeToken?.tokenAddress;
  const withSessionAction = useCallback((work: (operation: WalletOperation) => Promise<void>) => {
    if (sessionAction.current) return sessionAction.current;
    const operation = beginOperation();
    const pending = Promise.resolve().then(() => { operation.assertCurrent(); return work(operation); })
      .finally(() => { endOperation(operation); if (sessionAction.current === pending) sessionAction.current = null; });
    sessionAction.current = pending;
    return pending;
  }, [beginOperation, endOperation]);

  useEffect(() => {
    if (!miningController.getSnapshot().session || !miningController.discover || !walletSnapshot.authenticated || !discoveryToken) return;
    const operation = beginOperation();
    // Auth restoration performs only a server read. No hashing, signing or transfer.
    miningController.discover(discoveryToken, operation).catch(() => {
      // The controller retains an explicit unknown/check-status state. Auth errors
      // continue to belong to the existing wallet notification owner.
    }).finally(() => endOperation(operation)).catch(() => console.error("Mining discovery cleanup failed."));
    return () => operation.cancel();
  }, [walletSnapshot.authenticated, walletSnapshot.sessionRevision, discoveryToken,
    miningController, beginOperation, endOperation]);
  useEffect(() => {
    const pending = startup.current;
    if (pending && pending.scope !== activeContext.current.scopeKey) {
      pending.cancelled = true;
      pending.operation?.cancel();
      setMiningStarting(false);
    }
  }, [activeContext, contextKey]);
  useEffect(() => () => {
    const pending = startup.current;
    if (pending) { pending.cancelled = true; pending.operation?.cancel(); }
  }, []);

  useEffect(() => {
    const challenge = miningController.getSnapshot().challenge;
    if (!challenge) return;
    const walletMatches = Boolean(walletSnapshot.authenticated && walletSnapshot.account &&
      sameAddress(walletSnapshot.account, challenge.walletAddress));
    const chainMatches = walletSnapshot.chainId?.toString() === String(challenge.chainId);
    const tokenMatches = sameAddress(activeToken?.tokenAddress, challenge.tokenAddress);
    const miningAllowed = actionPermissions(bootstrap).mining.state === "ALLOWED";
    if (!walletMatches || !chainMatches || !tokenMatches || !miningAllowed) {
      miningController.pause(miningAllowed ? "WALLET_CONTEXT_CHANGED" : "MINING_DISABLED").catch(() => {
        // Execution is stopped before checkpoint persistence. A save failure
        // must remain observable without surfacing old-wallet data in this UI.
        console.error("Mining pause cleanup failed after a wallet context change.");
      });
    }
  }, [miningController, activeToken?.tokenAddress, walletSnapshot.account, walletSnapshot.authenticated, walletSnapshot.chainId, bootstrap]);

  const startMining = useCallback((): Promise<void> => {
    if (miningController.getSnapshot().session) return withSessionAction(async operation => {
      if (!activeToken || !miningController.discover || !miningController.createAttempt) throw new Error("MINING_CONTEXT_UNAVAILABLE");
      await miningController.discover(activeToken.tokenAddress, operation);
      operation.assertCurrent();
      const state = miningController.getSnapshot().session!;
      if (state.status === "ACTIVE" || state.status === "SOLVED" && miningController.getSnapshot().phase === "VERIFIED") return;
      if (actionPermissions(bootstrap).mining.state !== "ALLOWED" || isTransactionBlocked()) throw new Error("Mining is blocked by capability or pending transaction");
      await miningController.createAttempt(activeToken.tokenAddress, operation);
      operation.assertCurrent();
      // Creation leaves the attempt ready for an explicit Resume action.
    });
    const existing = startup.current;
    if (existing && !existing.cancelled && existing.scope === activeContext.current.scopeKey) return existing.promise;
    if (contextKey !== readContextKey()) return Promise.reject(new ContextChangedError());
    const audioGesture = miningAudio.unlock();
    const pending = { scope: activeContext.current.scopeKey, promise: Promise.resolve(), cancelled: false,
      operation: null as WalletOperation | null };
    const assertScope = () => {
      if (pending.cancelled || activeContext.current.scopeKey !== pending.scope ||
        !sameAddress(activeContext.current.token, activeToken?.tokenAddress)) throw new ContextChangedError();
    };
    // Assign the shared promise before connect/auth can notify subscribers or a
    // second click can enter. The lock includes the challenge request/configure.
    pending.promise = Promise.resolve().then(async () => {
      assertScope();
      if (contextKey !== readContextKey()) throw new ContextChangedError();
      const permission = actionPermissions(bootstrap).mining;
      if (!bootstrap) throw new Error(permission.reasons.join(", "));
      if (!activeToken) throw new Error("No registered source token is selected");
      if (!walletController.getSnapshot().connected) {
        await walletController.connect();
        assertScope();
      }
      // WalletSessionController fences its own connect/sign-in session changes;
      // keep token/deployment and account round trips fenced across them too.
      const identityRevision = walletController.getIdentityRevision();
      if (!walletController.getSnapshot().authenticated) {
        await walletController.authenticate();
        assertScope();
        if (walletController.getIdentityRevision() !== identityRevision) throw new ContextChangedError();
      }
      const operation = beginOperation();
      pending.operation = operation;
      try {
        assertScope();
        operation.assertCurrent();
        const authenticated = walletController.getSnapshot();
        if (!authenticated.authenticated || !authenticated.account) {
          throw new Error("Sign in before recovering or starting mining");
        }
        const current = miningController.getSnapshot();
        const sameContext = current.challenge && sameAddress(current.challenge.walletAddress, authenticated.account) &&
          sameAddress(current.challenge.tokenAddress, activeToken.tokenAddress) &&
          String(current.challenge.chainId) === String(bootstrap.sourceChainId);
        if (!sameContext) {
          const restored = await miningController.restore({ walletAddress: authenticated.account,
            chainId: String(bootstrap.sourceChainId), tokenAddress: activeToken.tokenAddress }, operation);
          assertScope();
          operation.assertCurrent();
          // Restore is read-only. Resume/retry remains a separate explicit action.
          if (restored) return;
        } else if ((current.candidate && !current.proof) || !["EXPIRED", "CANCELLED"].includes(current.phase)) {
          if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
          const audioRevision = miningAudio.arm("resume", readContextKey, audioGesture);
          try { await miningController.start({ guard: operation }); }
          catch (error) { if (audioRevision !== null) miningAudio.cancel(audioRevision); throw error; }
          return;
        }
        if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
        if (isTransactionBlocked()) throw new Error("Resolve the pending transaction before requesting a new challenge");
        const challenge = await walletController.api.post("/mining/challenges",
          { tokenAddress: activeToken.tokenAddress }, challengeSchema, operation.signal);
        assertScope();
        operation.assertCurrent();
        const owner = walletController.getSnapshot();
        if (!owner.authenticated || !owner.account ||
          !sameAddress(challenge.walletAddress, owner.account) ||
          !sameAddress(challenge.tokenAddress, activeToken.tokenAddress) ||
          String(challenge.chainId) !== String(bootstrap.sourceChainId)) {
          throw new Error("Server challenge identity does not match the active wallet, token, and chain");
        }
        if (isTransactionBlocked()) throw new Error("Resolve the pending transaction before changing the mining challenge");
        await miningController.configure({ challenge: challenge as MiningChallengeWire, guard: operation });
        assertScope();
        operation.assertCurrent();
        // POST may return an existing challenge. Preserve terminal/recovery states.
        if (challenge.status !== "ACTIVE" || miningController.getSnapshot().proof || miningController.getSnapshot().candidate) return;
        const audioRevision = miningAudio.arm("start", readContextKey, audioGesture);
        try { await miningController.start({ guard: operation }); }
        catch (error) { if (audioRevision !== null) miningAudio.cancel(audioRevision); throw error; }
      } finally { endOperation(operation); }
    }).finally(() => {
      if (startup.current === pending) {
        startup.current = null;
        if (!pending.cancelled) setMiningStarting(false);
      }
    });
    startup.current = pending;
    setMiningStarting(true);
    return pending.promise;
  }, [activeToken, bootstrap, beginOperation, endOperation, walletController, miningController,
    activeContext, contextKey, readContextKey, miningAudio, isTransactionBlocked, withSessionAction]);

  const retryMiningSubmission = useCallback(async () => {
    if (miningController.getSnapshot().session) return withSessionAction(async operation => {
      if (!activeToken || !miningController.discover) throw new Error("MINING_CONTEXT_UNAVAILABLE");
      // Reconcile before exact accepted-result retry; never rewrite provenance.
      const originalChallenge = miningController.getSnapshot().challenge;
      if (!originalChallenge || !sameAddress(originalChallenge.tokenAddress, activeToken.tokenAddress))
        throw new Error("Select the saved result's source token before retrying");
      await miningController.discover(activeToken.tokenAddress, operation); operation.assertCurrent();
      if (originalChallenge && miningController.getSnapshot().challenge?.challengeId !== originalChallenge.challengeId) {
        await miningController.configure({ challengeId: originalChallenge.challengeId, guard: operation }); operation.assertCurrent();
      }
      const snapshot = miningController.getSnapshot();
      if (snapshot.proof) return;
      if (snapshot.session?.status === "UNKNOWN" || snapshot.session?.status === "LEGACY_CONFLICT" || snapshot.session?.message)
        throw new Error("Check mining status before retrying");
      await miningController.retrySubmission({ guard: operation });
    });
    const snapshot = miningController.getSnapshot();
    const wallet = walletController.getSnapshot();
    const challenge = snapshot.challenge;
    if (!challenge || !wallet.authenticated || !wallet.account ||
      !sameAddress(wallet.account, challenge.walletAddress) ||
      String(wallet.chainId) !== String(challenge.chainId) ||
      !sameAddress(activeContext.current.token, challenge.tokenAddress)) {
      throw new Error("Connect and sign in with the proof-owner wallet on the challenge chain and source token before retrying");
    }
    if (!snapshot.candidate) throw new Error("No saved nonce is available to retry this submission");
    // The controller coalesces concurrent submissions and retains the original nonce.
    if (!["SUBMIT_RETRY", "SUBMITTING"].includes(snapshot.phase)) {
      throw new Error("There is no pending proof submission to retry");
    }
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    const audioGesture = miningAudio.unlock();
    const audioRevision = miningAudio.arm("retry", readContextKey, audioGesture);
    const operation = beginOperation();
    try { await miningController.retrySubmission({ guard: operation }); }
    catch (error) { if (audioRevision !== null) miningAudio.cancel(audioRevision); throw error; }
    finally { endOperation(operation); }
    // Failures reported as SUBMIT_RETRY are observed by audio synchronously.
    if (miningController.getSnapshot().phase === "SUBMIT_RETRY" && audioRevision !== null) miningAudio.cancel(audioRevision);
  }, [walletController, miningController, activeContext, contextKey, readContextKey, miningAudio, beginOperation, endOperation, withSessionAction, activeToken]);

  const resumeMining = useCallback(async () => {
    if (miningController.getSnapshot().session) return withSessionAction(async operation => {
      if (!activeToken || !miningController.discover) throw new Error("MINING_CONTEXT_UNAVAILABLE");
      if (actionPermissions(bootstrap).mining.state !== "ALLOWED") throw new Error("Mining is disabled by the server");
      await miningController.discover(activeToken.tokenAddress, operation); operation.assertCurrent();
      const snapshot = miningController.getSnapshot();
      if (snapshot.session?.status !== "ACTIVE" || snapshot.session.message) throw new Error("MINING_CURRENT_REQUIRES_RESOLUTION");
      // This explicit action permits transfer, including same-tab reauthentication.
      const audioGesture = miningAudio.unlock();
      const revision = miningAudio.arm("resume", readContextKey, audioGesture);
      try { await miningController.resume({ guard: operation, takeover: true, freshSearch: true }); }
      catch (error) { if (revision !== null) miningAudio.cancel(revision); throw error; }
    });
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    const permission = actionPermissions(bootstrap).mining;
    if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
    const operation = beginOperation();
    const audioGesture = miningAudio.unlock();
    const audioRevision = miningAudio.arm("resume", readContextKey, audioGesture);
    try { await miningController.resume({ guard: operation }); }
    catch (error) { if (audioRevision !== null) miningAudio.cancel(audioRevision); throw error; }
    finally { endOperation(operation); }
  }, [contextKey, readContextKey, beginOperation, endOperation, miningController, bootstrap, miningAudio, activeToken, withSessionAction]);

  const checkMiningStatus = useCallback(() => withSessionAction(async operation => {
    if (activeToken && miningController.discover) await miningController.discover(activeToken.tokenAddress, operation);
  }), [withSessionAction, activeToken, miningController]);
  const startNewMiningSession = useCallback(() => withSessionAction(async operation => {
    if (!activeToken || !miningController.createAttempt || isTransactionBlocked() ||
      actionPermissions(bootstrap).mining.state !== "ALLOWED") throw new Error("New mining session is unavailable");
    await miningController.createAttempt(activeToken.tokenAddress, operation);
  }), [withSessionAction, activeToken, miningController, isTransactionBlocked, bootstrap]);
  const checkSavedMiningProof = useCallback(() => withSessionAction(async operation => {
    if (isTransactionBlocked()) throw new Error("Resolve the pending transaction before checking a saved proof");
    const snapshot = miningController.getSnapshot();
    const wallet = walletController.getSnapshot();
    if (!activeToken || !wallet.authenticated || snapshot.challenge && (
      !sameAddress(snapshot.challenge.walletAddress, wallet.account) ||
      String(snapshot.challenge.chainId) !== String(wallet.chainId) ||
      !sameAddress(snapshot.challenge.tokenAddress, activeToken.tokenAddress))) throw new ContextChangedError();
    const results = snapshot.savedResults ?? [];
    if (!results.length || !miningController.checkSavedProof) return;
    operation.assertCurrent();
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    const scope = JSON.stringify([contextKey, wallet.sessionRevision, wallet.account?.toLowerCase(),
      wallet.chainId, activeToken.tokenAddress.toLowerCase()]);
    const byId = new Map(results.map(result => [result.challengeId.toLowerCase(), result]));
    const ids = [...byId.keys()];
    const selected = snapshot.challenge?.challengeId.toLowerCase() ?? null;
    const cursor = savedTraversal.current?.scope === scope ? savedTraversal.current :
      { scope, remaining: [], attempted: new Set<string>(), last: selected };
    // Keep unvisited keys in order across list refreshes. New keys join the round;
    // removed keys disappear from its queue, never from durable recovery storage.
    cursor.remaining = cursor.remaining.filter(id => byId.has(id));
    const queued = new Set(cursor.remaining);
    cursor.remaining.push(...ids.filter(id => !queued.has(id) && !cursor.attempted.has(id)));
    if (!cursor.attempted.size || !cursor.remaining.length) {
      const anchor = cursor.last && byId.has(cursor.last) ? cursor.last : selected;
      const index = anchor === null ? -1 : ids.indexOf(anchor);
      cursor.remaining = [...ids.slice(index + 1), ...ids.slice(0, index + 1)];
      cursor.attempted.clear();
    }
    const id = cursor.remaining.shift()!;
    cursor.attempted.add(id);
    cursor.last = id;
    // Advance once inside the shared action flight, before GET. A failed read
    // keeps its error/key but cannot trap later clicks. Late replies never write
    // this cursor or pretend that controller selection succeeded.
    savedTraversal.current = cursor;
    const saved = byId.get(id)!;
    await miningController.checkSavedProof(saved.proofId ?? saved.challengeId, operation, activeToken.tokenAddress);
  }), [withSessionAction, miningController, isTransactionBlocked, walletController, activeToken, contextKey, readContextKey]);

  return {
    startMining, retryMiningSubmission, resumeMining, miningStarting, authExpiring, checkMiningStatus, startNewMiningSession, checkSavedMiningProof,
    setMiningMode: (mode: MiningMode) => miningController.setMode(mode),
    setMiningPowerMode: (mode: MiningPowerMode) => miningController.getSnapshot().mode === "CPU" && miningController.getSnapshot().cpu ?
      miningController.setCpuWorkers(mode) : miningController.setPowerMode(mode),
    pauseMining: () => { miningAudio.cancel(); return miningController.pause(); },
    stopMining: () => { miningAudio.cancel(); return miningController.stop(); },
  };
}
